import { ThemeProvider, ToastProvider } from '@driftt/dds'
import { DriftAnalytics } from '@driftt/dds.analytics'
import { CubeDataProvider } from '@driftt/dds.reports'
import { pathOr } from 'ramda'
import React, { useEffect, useRef } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { useSelector } from 'react-redux'
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom'

import BlocksTable from 'components/BlocksTable'
import { FLCreationWizard } from 'components/Fastlane'
import IntelligentSearch from 'components/IntelligentSearch'
import NotFound from 'components/NotFound'
import PlaybooksTable from 'components/PlaybooksTable'
import ScenariosEditor from 'components/ScenariosEditor'
import ScenariosTable from 'components/ScenariosTable'
import SiteConcierge from 'components/SiteConcierge'
import config from 'config'
import AIConfiguration from 'layouts/AIConfiguration'
import BlockEditorLayout from 'layouts/BlockEditorLayout'
import CloneToolLayout from 'layouts/CloneToolLayout'
import EditorLayoutManager from 'layouts/EditorLayoutManager'
import GlobalComponentLibrary from 'layouts/GlobalComponents'
import GptNodeTestingLayout from 'layouts/GptNodeTesting'
import PlaybookPriorityLayout from 'layouts/PlaybookPriorityLayout'
import {
  scWindDown,
  userHasBionicChatbots,
  userHasChatPlaybooks,
  userHasContentVariables,
  userHasEmailPlaybooks,
  userHasFastlanePlaybooks,
  userHasMeetingsPlaybooks,
  userHasPlaybookPriorityAccess,
  userHasPlaybooksEditorAccess,
  userHasReusableBlocksV2,
} from 'utils/access'

import './style.css'

const queryClient = new QueryClient()

const ACCESS_BY_TYPE = {
  chat: () => userHasChatPlaybooks(),
  email: () => userHasEmailPlaybooks(),
  fastlane: () => userHasFastlanePlaybooks(),
  sequence: () => userHasEmailPlaybooks(),
  meetings: () => userHasMeetingsPlaybooks(),
}

const PlaybooksListIndex = () => {
  if (userHasChatPlaybooks()) {
    return <Redirect to={'/list/chat'} />
  } else if (userHasFastlanePlaybooks()) {
    return <Redirect to={'/list/fastlane'} />
  } else if (userHasEmailPlaybooks()) {
    return <Redirect to={'/list/email'} />
  } else if (userHasMeetingsPlaybooks()) {
    return <Redirect to={'/list/meetings'} />
  }

  window.location.href = `${config.CUSTOMER_WEB_URL}`
  return null
}

const PlaybooksTableByType = (props) => {
  const type = pathOr('', ['match', 'params', 'type'], props)

  return ACCESS_BY_TYPE[type]?.() ? (
    <PlaybooksTable {...props} />
  ) : (
    <NotFound {...props} />
  )
}

const AppLayout = () => {
  const initialized = useRef(false)
  const currentUser = useSelector((state) => pathOr(null, ['user'], state))
  useEffect(() => {
    if (initialized.current) {
      return
    }
    if (!currentUser) {
      return
    }

    DriftAnalytics.initialize({
      user: currentUser,
    })
    initialized.current = true
  }, [currentUser, initialized])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <CubeDataProvider>
            <Router basename="playbooks">
              <div className="playbook-editor-app-layout">
                <Switch>
                  <Redirect from="/list/fast-lane" to="/list/fastlane" />
                  <Route
                    exact
                    path="/clone-tool"
                    render={(props) => (
                      <CloneToolLayout queryClient={queryClient} {...props} />
                    )}
                  />
                  <Route
                    exact
                    path="/testing"
                    render={(props) => {
                      return <GptNodeTestingLayout {...props} />
                    }}
                  />
                  <Route
                    exact
                    path="/global/:type"
                    render={(props) =>
                      userHasContentVariables() ? (
                        <GlobalComponentLibrary
                          {...props}
                          queryClient={queryClient}
                        />
                      ) : (
                        <Redirect to={`/playbooks/`} />
                      )
                    }
                  />
                  <Route
                    exact
                    path="/admin/aiconfiguration"
                    render={(props) => (
                      <AIConfiguration {...props} queryClient={queryClient} />
                    )}
                  />
                  <Route
                    exact
                    path="/list/fastlane/create"
                    component={FLCreationWizard}
                  />
                  {scWindDown() ? (
                    <Route
                      component={IntelligentSearch}
                      path="/intelligent-search/:page"
                    />
                  ) : (
                    <Route
                      component={SiteConcierge}
                      path="/site-concierge/:page"
                    />
                  )}
                  <Route path="/list/:type" component={PlaybooksTableByType} />
                  <Route exact path="/list" component={PlaybooksListIndex} />
                  <Route
                    exact
                    path="/ai-scenarios"
                    render={(props) =>
                      userHasBionicChatbots() ? (
                        <ScenariosTable queryClient={queryClient} {...props} />
                      ) : (
                        <Redirect to={`/playbooks`} />
                      )
                    }
                  />
                  <Route
                    path="/ai-scenarios/editor/new-scenario/:templateId"
                    render={(props) =>
                      userHasBionicChatbots() ? (
                        <ScenariosEditor
                          queryClient={queryClient}
                          {...props}
                          isNewScenario
                        />
                      ) : (
                        <Redirect to={`/playbooks`} />
                      )
                    }
                  />
                  <Route
                    path="/ai-scenarios/editor/:id"
                    render={(props) =>
                      userHasBionicChatbots() ? (
                        <ScenariosEditor queryClient={queryClient} {...props} />
                      ) : (
                        <Redirect to={`/playbooks`} />
                      )
                    }
                  />
                  <Route
                    exact
                    path="/blocks/list"
                    render={(props) =>
                      userHasReusableBlocksV2() ? (
                        <BlocksTable queryClient={queryClient} {...props} />
                      ) : (
                        <Redirect to={`/playbooks`} />
                      )
                    }
                  />
                  <Route
                    path="/blocks/editor/:id/visual-builder"
                    render={(props) =>
                      userHasReusableBlocksV2() ? (
                        <BlockEditorLayout
                          queryClient={queryClient}
                          {...props}
                        />
                      ) : (
                        <Redirect to={`/playbooks`} />
                      )
                    }
                  />
                  <Route
                    path="/editor/:id"
                    render={(props) =>
                      userHasPlaybooksEditorAccess() ? (
                        <EditorLayoutManager {...props} />
                      ) : (
                        <Redirect to={`/playbooks`} />
                      )
                    }
                  />
                  <Route
                    exact
                    path="/priority/:type?"
                    render={(props) =>
                      userHasPlaybookPriorityAccess() ? (
                        <PlaybookPriorityLayout {...props} />
                      ) : (
                        <Redirect to={'/playbooks'} />
                      )
                    }
                  />
                  <Route path="/not-found" component={NotFound} />
                  <Route render={() => <Redirect to={'/list'} />} />
                </Switch>
              </div>
            </Router>
          </CubeDataProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default AppLayout
